// The find field of a list (module 10 U228; pipeline F1, B1950). It narrows the list as you type, 250 ms after the
// last key, with no Enter; Clear and Esc empty it at once. Before F1 Companies and People searched only on Enter, so
// typing alone did nothing, which read as no search at all (SEARCH-AND-FILTER-PLAN-2026-10-04.md, "What is there today").
import { ActionInput, ActionButton } from '../parts/ActionButton.js';
import { useEffect, useRef, useState } from 'react';
import './find.css';

/** How long after the last key the list is asked again: the plan's 250 ms, a little over the Deals find's 200. */
export const FIND_WAIT_MS = 250;

const ICON = <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>;

/**
 * `value` is what the list is narrowed to now (from the address); `onChange` is told the trimmed words once the
 * typing pauses, or at once on Clear, Esc or Enter. A value changed from outside (Back, the menu) replaces the words.
 */
export function FindField({ label, value, onChange, wait = FIND_WAIT_MS }: { label: string; value: string; onChange: (q: string) => void; wait?: number }) {
  const [typed, setTyped] = useState(value);
  const sent = useRef(value);
  const tell = useRef(onChange);
  tell.current = onChange;
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value === sent.current) return;
    sent.current = value;
    setTyped(value);
  }, [value]);

  const commit = (words: string) => {
    if (words === sent.current) return;
    sent.current = words;
    tell.current(words);
  };

  useEffect(() => {
    const words = typed.trim();
    if (words === sent.current) return undefined;
    const t = window.setTimeout(() => commit(words), wait);
    return () => window.clearTimeout(t);
  }, [typed, wait]);

  const clear = () => { setTyped(''); commit(''); input.current?.focus(); };

  return (
    <form className="field search find" role="search" onSubmit={(e) => { e.preventDefault(); commit(typed.trim()); }}>
      {ICON}
      <ActionInput reading ref={input} type="search" aria-label={label} placeholder={label} value={typed} autoComplete="off" spellCheck={false}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && typed !== '') { e.preventDefault(); clear(); } }} />
      {typed !== '' ? <ActionButton reading type="button" className="find-clear" onClick={clear}>Clear</ActionButton> : null}
    </form>
  );
}
